import { CompanyTag } from "@/components/domain/CompanyTag";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, Pencil, Plus, Search } from "lucide-react";
import {
  type QuotationSummaryDto,
  type QuotationListInput,
  type Paginated,
} from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { Badge } from "@/components/ui/Badge";
import {
  EmptyTableRow,
  ListTableFrame,
  LIST_PAGE_BODY,
} from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { Stack } from "@/components/ui/Stack";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useQuotations, useQuotationCustomerOptions } from "@/api/hooks";
import { formatDate } from "@/domain/datetime";
import { useClampListPage, useListQueryState, useParamInput } from "@/lib/listQuery";
import { apiFetch } from "@/lib/api";
import { quotationListColumns, quotationStatus } from "@/lib/quotationCsv";
import { ExportButton } from "@/components/domain/ExportButton";

export function QuotationsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const list = useListQueryState();
  const currencyInput = useParamInput(list, "currency", (value) => value.trim().toUpperCase());
  const preparedByInput = useParamInput(list, "preparedBy");
  const context = list.searchParams.size ? `?${list.searchParams}` : "";
  const archived = list.searchParams.get("archived") === "true";
  const from = list.searchParams.get("from") ?? "";
  const to = list.searchParams.get("to") ?? "";
  const requestedStatus = list.searchParams.get("status");
  const filters: Partial<QuotationListInput> = {
    q: list.q.trim() || undefined,
    page: list.page,
    pageSize: list.pageSize,
    customerId: list.searchParams.get("customerId") || undefined,
    preparedBy: list.searchParams.get("preparedBy") || undefined,
    currency: list.searchParams.get("currency") || undefined,
    status:
      requestedStatus === "Draft" ||
      requestedStatus === "Issued" ||
      requestedStatus === "Expired"
        ? requestedStatus
        : undefined,
    from: from ? new Date(from + "T00:00:00").getTime() : undefined,
    to: to ? new Date(to + "T23:59:59.999").getTime() : undefined,
    sort: list.searchParams.get("sort") || "-issueDate",
    archived: archived ? "true" : "false",
  };
  const result = useQuotations(filters);
  useClampListPage(result.data?.total, list.clampPage);
  const rows = result.data?.data ?? [];
  const customers = useQuotationCustomerOptions();
  const customerOptions = (customers.data ?? []).map((customer) => ({
    value: customer.id,
    label: customer.companyName,
  }));
  if (
    filters.customerId &&
    !customerOptions.some((customer) => customer.value === filters.customerId)
  ) {
    customerOptions.push({
      value: filters.customerId,
      label:
        rows.find((row) => row.customerId === filters.customerId)
          ?.customerName || t("quotationWorkspace.unavailableCustomer"),
    });
  }
  const canCreate = Boolean(user && can("records:create"));
  const canUpdate = Boolean(user && can("records:update"));
  return (
    <PageBody className={LIST_PAGE_BODY}>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          {...list.searchInput}
          leading={<Search />}
          aria-label={t("common.search")}
          placeholder={t("common.search")}
          className="w-full max-w-xs"
        />
        <Select
          aria-label={t("quotationWorkspace.lifecycle")}
          value={archived ? "true" : "false"}
          onChange={(event) =>
            list.updateParam(
              "archived",
              event.target.value === "true" ? "true" : null,
            )
          }
        >
          <option value="false">{t("quotationWorkspace.active")}</option>
          <option value="true">{t("quotationWorkspace.archived")}</option>
        </Select>
        <div className="ml-auto">
          <ExportButton<QuotationSummaryDto>
            name="quotations"
            title={t("nav.quotations")}
            rows={rows}
            total={result.data?.total}
            disabled={result.isFetching}
            errorKey="quotations.errorLoad"
            columns={quotationListColumns(t)}
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<QuotationSummaryDto>>("/quotations", {
                query: { ...filters, page, pageSize },
                signal,
              })
            }
          />
        </div>
        {canCreate && (
          <Button
            size="sm"
            onClick={() => navigate(`/quotations/new${context}`)}
          >
            <Plus className="size-4" />
            {t("quotations.new")}
          </Button>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <SearchableFilter
          label={t("common.customer")}
          value={filters.customerId ?? ""}
          options={customerOptions}
          allLabel={t("quotationWorkspace.allCustomers")}
          onChange={(value) => list.updateParam("customerId", value || null)}
          isLoading={customers.isLoading}
          error={
            customers.error ? t("quotationWorkspace.customerLoadError") : null
          }
          onRetry={() => void customers.refetch()}
        />
        <Select
          aria-label={t("common.status")}
          value={filters.status ?? ""}
          onChange={(event) =>
            list.updateParam("status", event.target.value || null)
          }
        >
          <option value="">{t("quotationWorkspace.allStatuses")}</option>
          {["Draft", "Issued", "Expired"].map((status) => (
            <option key={status} value={status}>
              {t(`quotationWorkspace.status_${status}`)}
            </option>
          ))}
        </Select>
        <Input
          className="w-24"
          aria-label={t("common.currencyLabel")}
          placeholder={t("common.currencyLabel")}
          {...currencyInput}
        />
        <Input
          className="w-40"
          aria-label={t("quotations.preparedBy")}
          placeholder={t("quotations.preparedBy")}
          {...preparedByInput}
        />
        <Input
          type="date"
          aria-label={t("quotationWorkspace.from")}
          value={from}
          onChange={(event) =>
            list.updateParam("from", event.target.value || null)
          }
        />
        <Input
          type="date"
          aria-label={t("quotationWorkspace.to")}
          value={to}
          onChange={(event) =>
            list.updateParam("to", event.target.value || null)
          }
        />
        <Select
          aria-label={t("quotationWorkspace.sort")}
          value={filters.sort}
          onChange={(event) => list.updateParam("sort", event.target.value)}
        >
          <option value="-issueDate">{t("quotationWorkspace.newest")}</option>
          <option value="issueDate">{t("quotationWorkspace.oldest")}</option>
          <option value="quotationNo">{t("quotations.no")} ↑</option>
          <option value="-quotationNo">{t("quotations.no")} ↓</option>
          <option value="-createdAt">{t("common.created")} ↓</option>
        </Select>
      </div>
      {from && to && from > to && (
        <p role="alert" className="text-xs text-danger">
          {t("quotationWorkspace.invalidDateRange")}
        </p>
      )}
      <ListTableFrame
        isLoading={result.isLoading}
        isFetching={result.isFetching}
        error={result.error}
        errorMessage={t("quotations.errorLoad")}
        onRetry={() => void result.refetch()}
        page={result.data?.page ?? list.page}
        pageSize={result.data?.pageSize ?? list.pageSize}
        total={result.data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            <Th>#</Th>
            <Th pin>{t("quotations.no")}</Th>
            <Th>{t("common.status")}</Th>
            <Th>{t("common.customer")}</Th>
            <Th>{t("pi.date")}</Th>
            <Th>{t("quotationWorkspace.validUntil")}</Th>
            <Th>{t("common.currencyLabel")}</Th>
            <Th>{t("quotations.preparedBy")}</Th>
            <Th>{t("quotations.lines")}</Th>
            <Th>{t("common.remarks")}</Th>
            <Th>{t("common.created")}</Th>
            <Th pinRight align="right">
              {t("common.actions")}
            </Th>
          </tr>
        </thead>
        <tbody>
          {!rows.length && (
            <EmptyTableRow colSpan={12}>{t("common.noMatches")}</EmptyTableRow>
          )}
          {rows.map((row, index) => {
            const status = quotationStatus(row);
            return (
              <Tr key={row.id} className="group/row" interactive>
                <Td className="font-mono text-xs text-ink-faint">
                  {((result.data?.page ?? list.page) - 1) *
                    (result.data?.pageSize ?? list.pageSize) +
                    index +
                    1}
                </Td>
                <Td pin>
                  <Link
                    className="font-mono font-medium text-brand hover:underline"
                    to={`/quotations/${row.id}${context}`}
                  >
                    {row.quotationNo}
                  </Link>
                  <p className="mt-1 text-2xs text-ink-faint">
                    {t("quotationWorkspace.revision")}{" "}
                    <span className="font-mono">{row.revision}</span>
                  </p>
                </Td>
                <Td>
                  <Badge
                    tone={
                      status === "Expired"
                        ? "warn"
                        : status === "Issued"
                          ? "info"
                          : "neutral"
                    }
                  >
                    {t(
                      row.workflowVersion === 0
                        ? "quotationWorkspace.legacyReview"
                        : `quotationWorkspace.status_${status}`,
                    )}
                  </Badge>
                </Td>
                <Td className="max-w-64 whitespace-normal py-2 text-xs">
                  <CompanyTag region={row.customerRegion} className="mr-1.5" />
                  {row.customerName ||
                    row.billToName ||
                    t("quotations.unknownCustomer")}
                </Td>
                <Td className="font-mono text-xs">
                  {formatDate(row.issueDate)}
                </Td>
                <Td className="font-mono text-xs">
                  {formatDate(row.validUntil)}
                </Td>
                <Td className="font-mono">{row.currency}</Td>
                <Td className="text-xs text-ink-soft">
                  {row.preparedBy || "—"}
                </Td>
                <Td className="max-w-64">
                  <Stack
                    monoTop
                    top={String(row.itemCount)}
                    bottom={row.itemPreview}
                  />
                </Td>
                <Td className="max-w-64">
                  <span
                    className="block truncate text-xs text-ink-soft"
                    title={row.remarks ?? undefined}
                  >
                    {row.remarks || "—"}
                  </span>
                </Td>
                <Td>
                  <Stack
                    monoTop
                    top={formatDate(row.createdAt)}
                    bottom={row.createdByName}
                  />
                </Td>
                <Td pinRight align="right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`${t("quotationWorkspace.view")}: ${row.quotationNo}`}
                      onClick={() =>
                        navigate(`/quotations/${row.id}${context}`)
                      }
                    >
                      <Eye className="size-3.5" />
                    </Button>
                    {canUpdate && !archived && row.status === "Draft" && (
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`${t("common.edit")}: ${row.quotationNo}`}
                        onClick={() =>
                          navigate(`/quotations/${row.id}/edit${context}`)
                        }
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    )}
                  </div>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
